'use client';

import { NextIntlClientProvider } from 'next-intl';
import { useAuthStore } from '@/hooks/use-auth';
import { TooltipProvider } from '@/components/ui/tooltip';
import { SUPPORTED_LOCALES, type SupportedLocale } from '@/lib/validators/user.schema';
import en from '@/messages/en.json';
import zhCN from '@/messages/zh-CN.json';
import ko from '@/messages/ko.json';
import ja from '@/messages/ja.json';

// Non-en locales are cast through unknown (zh-CN drift; ko/ja AI-drafted,
// full-parity but flagged unreviewed) so a structural mismatch doesn't block
// the build. Mirrors admin-i18n-provider.tsx / request.ts.
const messages: Record<string, typeof en> = {
  en,
  'zh-CN': zhCN as unknown as typeof en,
  ko: ko as unknown as typeof en,
  ja: ja as unknown as typeof en,
};

function isSupportedLocale(value: string | undefined | null): value is SupportedLocale {
  return value != null && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * i18n provider for the public/marketing surface. Unlike AdminI18nProvider, it
 * works without an authenticated user: its initial locale comes from the
 * `NEXT_LOCALE` cookie read server-side in the `(public)` layout and passed in
 * as `initialLocale`, so SSR renders the correct language with no English flash.
 *
 * An authenticated user's account `preferredLanguage` takes precedence when
 * present. No effect is needed: on both SSR and the first client render the
 * auth store is empty (user null) → `initialLocale` (cookie) is used, so the
 * markup matches and there's no hydration mismatch. Once `restoreSession`
 * populates the user, the store update triggers a natural re-render that prefers
 * their preference (mirrors AdminI18nProvider's effect-free approach).
 */
export function PublicI18nProvider({
  initialLocale,
  children,
}: {
  initialLocale: SupportedLocale;
  children: React.ReactNode;
}) {
  const userLanguage = useAuthStore((s) => s.user?.preferredLanguage);
  const locale = isSupportedLocale(userLanguage) ? userLanguage : initialLocale;

  return (
    <NextIntlClientProvider locale={locale} messages={messages[locale] ?? messages.en}>
      <TooltipProvider>{children}</TooltipProvider>
    </NextIntlClientProvider>
  );
}
